import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { insertMultimodalModal, removeMultimodalModal } from '@/utils/Page/modal';
import DetailModal from '@/pages/Web/PageComponents/MultimodalLearning/DetailModal';
import grtStore from '@/store/grtStore';

// 事件名：用于在多条消息之间广播"哪个视频列表被展开了"
const VIDEO_LIST_TOGGLE_EVENT = 'grt-multimodal-video-list-toggle';

/**
 * 多模态详情 Modal 管理 Hook
 */
const useMultimodalDetailModal = ({ messageId, data = [], createVideoHandlersFn }) => {
    const [detailData, setDetailData] = useState(null);
    const [showVideoList, setShowVideoList] = useState(false);
    const [isModalActive, setIsModalActive] = useState(false);
    const [isVideoListOpenForThisMessage, setIsVideoListOpenForThisMessage] = useState(false);

    const prevDetailDataRef = useRef(null);
    const prevShowVideoListRef = useRef(null);
    const handlersRef = useRef({});
    const handleExpandClickRef = useRef(null);

    // 视频列表
    const videoList = useMemo(() => {
        const rawVideoList = Array.isArray(data) && data.length > 0 ? data : [];
        return rawVideoList.map((video) => ({
            ...video,
            thumbnail: video.image_url || video.thumbnail || '',
            image_url: video.image_url || video.thumbnail || ''
        }));
    }, [data?.length, data?.[0]?.id, data?.[0]?.title]);

    // 关闭 Modal（提前定义，避免变量提升问题）
    const handleClose = useCallback(() => {
        setDetailData(null);
        setShowVideoList(false);
        setIsModalActive(false);
        setIsVideoListOpenForThisMessage(false);

        if (typeof window !== 'undefined' && window.dispatchEvent) {
            window.dispatchEvent(
                new CustomEvent(VIDEO_LIST_TOGGLE_EVENT, {
                    detail: { messageId: null }
                })
            );
        }

        removeMultimodalModal('multimodal-detail-drawer');
        prevDetailDataRef.current = null;
        prevShowVideoListRef.current = null;

        // 如果没有外部传入 setter，则使用全局 store
        const multimodalStore = grtStore?.multimodalStore;
        const setDetailPanelOpenFn = multimodalStore?.setDetailPanelOpen;
        const setActiveDetailMessageIdFn = multimodalStore?.setActiveMessageId;
        if (setDetailPanelOpenFn && setActiveDetailMessageIdFn) {
            setDetailPanelOpenFn(false);
            setActiveDetailMessageIdFn(null);
        }
    }, []);

    // 打开视频列表
    const handleExpandClick = useCallback(() => {
        const rawVideoList = Array.isArray(data) && data.length > 0 ? data : [];
        const videoList = rawVideoList.map((video) => ({
            ...video,
            thumbnail: video.image_url || video.thumbnail || '',
            image_url: video.image_url || video.thumbnail || ''
        }));

        const videoListData = {
            title: '视频列表',
            type: 'video_list',
            description: ''
        };

        setDetailData(videoListData);
        setShowVideoList(true);
        setIsVideoListOpenForThisMessage(true);

        if (typeof window !== 'undefined' && window.dispatchEvent) {
            window.dispatchEvent(
                new CustomEvent(VIDEO_LIST_TOGGLE_EVENT, {
                    detail: { messageId }
                })
            );
        }

        const multimodalStore = grtStore?.multimodalStore;
        const setDetailPanelOpenFn = multimodalStore?.setDetailPanelOpen;
        const setActiveDetailMessageIdFn = multimodalStore?.setActiveMessageId;

        if (setDetailPanelOpenFn && setActiveDetailMessageIdFn) {
            setDetailPanelOpenFn(true);
            setActiveDetailMessageIdFn(messageId);
        }

        setIsModalActive(true);
        const currentHandlers = handlersRef.current;
        insertMultimodalModal(
            <DetailModal
                visible={true}
                data={videoListData}
                onClose={handleClose}
                videoList={videoList}
                onVideoClick={currentHandlers.handleVideoClick}
                onDetailClick={currentHandlers.handleVideoDetailClick}
                onContinueAsk={currentHandlers.handleContinueAsk}
                onViewNext={currentHandlers.handleViewNext}
                onBackToList={currentHandlers.handleBackToList}
            />,
            'multimodal-detail-drawer'
        );
    }, [data, messageId, handleClose]);

    handleExpandClickRef.current = handleExpandClick;

    // 创建 videoHandlers
    const videoHandlers = useMemo(() => {
        if (typeof createVideoHandlersFn === 'function') {
            return createVideoHandlersFn(setDetailData, setShowVideoList, () => handleExpandClickRef.current?.());
        }
        return {};
    }, [createVideoHandlersFn, setDetailData, setShowVideoList]);

    // 更新 handlersRef
    useEffect(() => {
        handlersRef.current = {
            handleClose,
            ...videoHandlers
        };
    }, [videoHandlers, handleClose]);

    // 打开详情 Modal
    const openDetailModal = useCallback(
        (ele) => {
            setDetailData(ele);
            setShowVideoList(false);
            setIsVideoListOpenForThisMessage(false);

            if (typeof window !== 'undefined' && window.dispatchEvent) {
                window.dispatchEvent(
                    new CustomEvent(VIDEO_LIST_TOGGLE_EVENT, {
                        detail: { messageId: null }
                    })
                );
            }

            const multimodalStore = grtStore?.multimodalStore;
            const setDetailPanelOpenFn = multimodalStore?.setDetailPanelOpen;
            const setActiveDetailMessageIdFn = multimodalStore?.setActiveMessageId;

            if (setDetailPanelOpenFn && setActiveDetailMessageIdFn) {
                setDetailPanelOpenFn(true);
                setActiveDetailMessageIdFn(messageId);
            }

            setIsModalActive(true);
            const rawVideoList = Array.isArray(data) && data.length > 0 ? data : [];
            const mappedVideoList = rawVideoList.map((video) => ({
                ...video,
                thumbnail: video.image_url || video.thumbnail || '',
                image_url: video.image_url || video.thumbnail || ''
            }));

            insertMultimodalModal(
                <DetailModal
                    visible={true}
                    data={ele}
                    onClose={handleClose}
                    videoList={mappedVideoList}
                    onVideoClick={videoHandlers.handleVideoClick}
                    onDetailClick={videoHandlers.handleVideoDetailClick}
                    onContinueAsk={videoHandlers.handleContinueAsk}
                    onViewNext={videoHandlers.handleViewNext}
                    onBackToList={videoHandlers.handleBackToList}
                />,
                'multimodal-detail-drawer'
            );
        },
        [data, messageId, handleClose, videoHandlers]
    );

    // 监听全局事件
    useEffect(() => {
        if (typeof window === 'undefined' || !window.addEventListener) return;

        const handler = (event) => {
            const targetId = event?.detail?.messageId;
            // normalize to string to avoid mismatched types (number vs string)
            const isOpen = !!targetId && String(targetId) === String(messageId);
            setIsVideoListOpenForThisMessage(isOpen);
        };

        window.addEventListener(VIDEO_LIST_TOGGLE_EVENT, handler);
        return () => {
            window.removeEventListener(VIDEO_LIST_TOGGLE_EVENT, handler);
        };
    }, [messageId]);
    // 监听状态变化，更新 Modal
    useEffect(() => {
        if (isModalActive && detailData) {
            const detailDataKey = JSON.stringify({
                type: detailData.type,
                title: detailData.title,
                id: detailData.id || detailData.resource_id,
                videoLoading: detailData.type === 'video_detail' ? detailData.video?.loading : undefined
            });
            const prevDetailDataKey = prevDetailDataRef.current
                ? JSON.stringify({
                      type: prevDetailDataRef.current.type,
                      title: prevDetailDataRef.current.title,
                      id: prevDetailDataRef.current.id || prevDetailDataRef.current.resource_id,
                      videoLoading: prevDetailDataRef.current.type === 'video_detail' ? prevDetailDataRef.current.video?.loading : undefined
                  })
                : null;

            const shouldUpdate = detailDataKey !== prevDetailDataKey || showVideoList !== prevShowVideoListRef.current;

            if (shouldUpdate) {
                prevDetailDataRef.current = detailData;
                prevShowVideoListRef.current = showVideoList;

                insertMultimodalModal(
                    <DetailModal
                        visible={true}
                        data={detailData}
                        onClose={handlersRef.current.handleClose}
                        videoList={videoList}
                        onVideoClick={handlersRef.current.handleVideoClick}
                        onDetailClick={handlersRef.current.handleVideoDetailClick}
                        onContinueAsk={handlersRef.current.handleContinueAsk}
                        onViewNext={handlersRef.current.handleViewNext}
                        onBackToList={handlersRef.current.handleBackToList}
                    />,
                    'multimodal-detail-drawer'
                );
            }
        } else if (isModalActive && !detailData) {
            removeMultimodalModal('multimodal-detail-drawer');
            setIsModalActive(false);
            prevDetailDataRef.current = null;
            prevShowVideoListRef.current = null;
        }
    }, [isModalActive, detailData, showVideoList, videoList]);

    // 监听父组件状态变化
    // useEffect(() => {
    //     const multimodalStore = grtStore?.multimodalStore;
    //     const activeDetailMessageId = multimodalStore?.activeMessageId;
    //     const detailPanelOpen = multimodalStore?.detailPanelOpen;

    //     const isActiveMismatch = activeDetailMessageId && activeDetailMessageId !== messageId;
    //     const hasMultimodalData = Array.isArray(data) && data.length > 0;

    //     if (hasMultimodalData && detailPanelOpen) {
    //         const setActiveDetailMessageIdFn = multimodalStore?.setActiveMessageId;
    //         if (!activeDetailMessageId && setActiveDetailMessageIdFn) {
    //             setActiveDetailMessageIdFn(messageId);
    //             return;
    //         }
    //     }

    //     if (isActiveMismatch) {
    //         setDetailData(null);
    //         setShowVideoList(false);
    //         const setDetailPanelOpenFn = multimodalStore?.setDetailPanelOpen;
    //         if (setDetailPanelOpenFn) {
    //             setDetailPanelOpenFn(false);
    //         }
    //     }
    // }, [messageId, data]);

    // 组件卸载时清理
    useEffect(() => {
        return () => {
            removeMultimodalModal('multimodal-detail-drawer');
        };
    }, []);

    const isCurrentDrawerOpen = isVideoListOpenForThisMessage;

    return {
        detailData,
        setDetailData,
        showVideoList,
        setShowVideoList,
        openDetailModal,
        handleExpandClick,
        handleClose,
        isCurrentDrawerOpen
    };
};

export default useMultimodalDetailModal;
